import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Chip,
  OutlinedInput,
  ListItemText,
  TableCell,
  TableRow,
  Paper,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../config/api';
import DataTable from '../components/DataTable';
import * as XLSX from 'xlsx';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    source_website: '',
    state_id: '',
    district_id: '',
    city_id: '',
    area: '',
    pincode: '',
    start_plan: '',
    available_days: '',
    service_type: 'home_services',
    category_id: '',
    subcategory_ids: [],
  });
  const [error, setError] = useState('');
  
  // Filter states
  const [filterState, setFilterState] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchStates();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (filterState) {
      fetchDistrictsForFilter(filterState);
    } else {
      setFilterDistricts([]);
      setFilterDistrict('');
    }
  }, [filterState]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/users-list');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/locations/states');
      setStates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setDistricts(response.data.data || []);
      setCities([]); // Reset cities when district changes
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchDistrictsForFilter = async (stateId) => {
    if (!stateId) {
      setFilterDistricts([]);
      return;
    }
    try {
      const response = await api.get(`/locations/states/${stateId}/districts`);
      setFilterDistricts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching districts for filter:', error);
    }
  };

  const fetchCities = async (districtId) => {
    if (!districtId) {
      setCities([]);
      return;
    }
    try {
      const response = await api.get(`/locations/districts/${districtId}/cities`);
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.data || []);
      // Flatten all subcategories
      const allSubcategories = [];
      response.data.data.forEach(cat => {
        if (cat.subcategories) {
          cat.subcategories.forEach(sub => {
            allSubcategories.push({ ...sub, category_name: cat.name });
          });
        }
      });
      setSubcategories(allSubcategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const getSubcategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    return subcategories.filter(sub => sub.category_id === parseInt(categoryId));
  };

  const handleOpen = async (user = null) => {
    if (user) {
      setEditing(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        gender: user.gender || '',
        source_website: user.source_website || '',
        state_id: user.state_id || '',
        district_id: user.district_id || '',
        city_id: user.city_id || '',
        area: user.area || '',
        pincode: user.pincode || '',
        start_plan: user.start_plan || '',
        available_days: user.available_days || '',
        service_type: user.service_type || 'home_services',
        category_id: user.category_id || '',
        subcategory_ids: user.subcategories ? user.subcategories.map(s => s.id) : [],
      });
      // Fetch districts and cities if IDs are available
      if (user.state_id) {
        await fetchDistricts(user.state_id);
      }
      if (user.district_id) {
        await fetchCities(user.district_id);
      }
    } else {
      setEditing(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        gender: '',
        source_website: '',
        state_id: '',
        district_id: '',
        city_id: '',
        area: '',
        pincode: '',
        start_plan: '',
        available_days: '',
        service_type: 'home_services',
        category_id: '',
        subcategory_ids: [],
      });
      setDistricts([]);
      setCities([]);
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      gender: '',
      source_website: '',
      state_id: '',
      district_id: '',
      city_id: '',
      area: '',
      pincode: '',
      start_plan: '',
      available_days: '',
      service_type: 'home_services',
      category_id: '',
      subcategory_ids: [],
    });
    setDistricts([]);
    setCities([]);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const dataToSend = {
        ...formData,
        subcategory_ids: formData.subcategory_ids && formData.subcategory_ids.length > 0
          ? formData.subcategory_ids
          : null
      };
      if (editing) {
        await api.put(`/users/${editing.id}`, dataToSend);
      } else {
        await api.post('/users', dataToSend);
      }
      handleClose();
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving user');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/users/${id}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleViewOpen = (user) => {
    setViewUser(user);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewUser(null);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilterState('');
    setFilterDistrict('');
    setFilterArea('');
    setFilterGender('');
    setFilterEmail('');
    setFilterPhone('');
    setFilterDistricts([]);
  };

  // Filter users based on state, district, and area
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    if (filterState) {
      filtered = filtered.filter(user => user.state_id === parseInt(filterState));
    }

    if (filterDistrict) {
      filtered = filtered.filter(user => user.district_id === parseInt(filterDistrict));
    }

    if (filterArea) {
      const areaLower = filterArea.toLowerCase();
      filtered = filtered.filter(user => 
        user.area && user.area.toLowerCase().includes(areaLower)
      );
    }

    if (filterGender) {
      filtered = filtered.filter(user => (user.gender || '').toLowerCase() === filterGender.toLowerCase());
    }

    if (filterEmail) {
      const emailSearch = filterEmail.toLowerCase();
      filtered = filtered.filter(user => (user.email || '').toLowerCase().includes(emailSearch));
    }

    if (filterPhone) {
      const phoneSearch = filterPhone.toLowerCase();
      filtered = filtered.filter(user => (user.phone || '').toLowerCase().includes(phoneSearch));
    }

    return filtered;
  }, [users, filterState, filterDistrict, filterArea, filterGender, filterEmail, filterPhone]);

  const genderCounts = useMemo(() => {
    return users.reduce((acc, user) => {
      const key = (user.gender || '').toLowerCase();
      if (key) {
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {});
  }, [users]);

  // Export users to Excel
  const handleExportExcel = async () => {
    // Fetch all districts for all states that are in the filtered users
    const stateIds = [...new Set(filteredUsers.map(u => u.state_id).filter(Boolean))];
    const districtsMap = new Map();
    
    // Fetch districts for each state
    for (const stateId of stateIds) {
      try {
        const response = await api.get(`/locations/states/${stateId}/districts`);
        const districtList = response.data.data || [];
        districtList.forEach(district => {
          districtsMap.set(district.id, district.name);
        });
      } catch (error) {
        console.error(`Error fetching districts for state ${stateId}:`, error);
      }
    }

    const exportData = filteredUsers.map(user => ({
      'ID': user.id,
      'Name': user.name || '',
      'Email': user.email || '',
      'Phone': user.phone || '',
      'Gender': user.gender || '',
      'State': states.find(s => s.id === user.state_id)?.name || '',
      'District': districtsMap.get(user.district_id) || '',
      'Area': user.area || '',
      'Pincode': user.pincode || '',
      'Service Type': user.service_type || '',
      'Start Plan': user.start_plan || '',
      'Available Days': user.available_days || '',
      'Source Website': user.source_website || '',
      'Category': categories.find(c => c.id === user.category_id)?.name || '',
      'Subcategories': user.subcategories?.map(s => s.name).join(', ') || '',
    }));

    if (exportData.length === 0) {
      alert('No data to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    
    // Auto-size columns
    const maxWidth = 50;
    const wscols = Object.keys(exportData[0] || {}).map(() => ({ wch: maxWidth }));
    ws['!cols'] = wscols;

    XLSX.writeFile(wb, `users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Handle Excel file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadError('');
    setUploadSuccess('');

    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
      });

      // Validate and process the data
      const usersToCreate = [];
      const errors = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        const rowNum = i + 2; // +2 because Excel is 1-indexed and has header

        // Required fields validation
        if (!row.Name || !row.Email) {
          errors.push(`Row ${rowNum}: Name and Email are required`);
          continue;
        }

        // Find state_id if state name is provided
        let stateId = null;
        if (row.State) {
          const state = states.find(s => 
            s.name.toLowerCase() === row.State.toLowerCase().trim()
          );
          if (state) {
            stateId = state.id;
          } else {
            errors.push(`Row ${rowNum}: State "${row.State}" not found`);
          }
        }

        // Find district_id if district name is provided
        let districtId = null;
        if (row.District && stateId) {
          // First check filterDistricts (if state matches current filter)
          let district = filterDistricts.find(d => 
            d.name.toLowerCase() === row.District.toLowerCase().trim()
          );
          
          if (!district) {
            // Try fetching districts for the state
            try {
              const distResponse = await api.get(`/locations/states/${stateId}/districts`);
              const districtList = distResponse.data.data || [];
              district = districtList.find(d => 
                d.name.toLowerCase() === row.District.toLowerCase().trim()
              );
            } catch (error) {
              errors.push(`Row ${rowNum}: Error fetching districts for state`);
            }
          }
          
          if (district) {
            districtId = district.id;
          } else {
            errors.push(`Row ${rowNum}: District "${row.District}" not found`);
          }
        }

        // Find category_id if category name is provided
        let categoryId = null;
        if (row.Category) {
          const category = categories.find(c => 
            c.name.toLowerCase() === row.Category.toLowerCase().trim()
          );
          if (category) {
            categoryId = category.id;
          } else {
            errors.push(`Row ${rowNum}: Category "${row.Category}" not found`);
          }
        }

        // Process subcategories
        let subcategoryIds = [];
        if (row.Subcategories && categoryId) {
          const subcatNames = row.Subcategories.split(',').map(s => s.trim());
          const categorySubcats = subcategories.filter(s => s.category_id === categoryId);
          subcatNames.forEach(subcatName => {
            const subcat = categorySubcats.find(s => 
              s.name.toLowerCase() === subcatName.toLowerCase()
            );
            if (subcat) {
              subcategoryIds.push(subcat.id);
            }
          });
        }

        usersToCreate.push({
          name: row.Name.trim(),
          email: row.Email.trim(),
          phone: row.Phone?.toString().trim() || '',
          gender: row.Gender?.toLowerCase() || '',
          state_id: stateId || '',
          district_id: districtId || '',
          area: row.Area?.trim() || '',
          pincode: row.Pincode?.toString().trim() || '',
          service_type: row['Service Type']?.toLowerCase() === 'online_services' ? 'online_services' : 'home_services',
          start_plan: row['Start Plan']?.toLowerCase() || '',
          available_days: row['Available Days']?.toLowerCase() || '',
          source_website: row['Source Website']?.trim() || '',
          category_id: categoryId || '',
          subcategory_ids: subcategoryIds.length > 0 ? subcategoryIds : null,
        });
      }

      if (usersToCreate.length > 0) {
        // Check for duplicates within the Excel file
        const emailSet = new Set();
        const duplicateEmailsInFile = [];
        const uniqueUsersToCreate = [];

        usersToCreate.forEach((userData, index) => {
          const emailLower = userData.email.toLowerCase();
          if (emailSet.has(emailLower)) {
            duplicateEmailsInFile.push(`Row ${index + 2}: Email "${userData.email}" is duplicated in the file`);
          } else {
            emailSet.add(emailLower);
            uniqueUsersToCreate.push(userData);
          }
        });

        if (duplicateEmailsInFile.length > 0) {
          errors.push(...duplicateEmailsInFile);
        }

        // Check for duplicates against existing users
        const existingEmails = new Set(users.map(u => u.email?.toLowerCase()).filter(Boolean));
        const duplicateEmailsInDB = [];
        const finalUsersToCreate = [];

        uniqueUsersToCreate.forEach((userData, index) => {
          const emailLower = userData.email.toLowerCase();
          if (existingEmails.has(emailLower)) {
            duplicateEmailsInDB.push(`Row ${index + 2}: Email "${userData.email}" already exists in the system`);
          } else {
            finalUsersToCreate.push(userData);
            existingEmails.add(emailLower); // Track to avoid duplicates in the same batch
          }
        });

        if (duplicateEmailsInDB.length > 0) {
          errors.push(...duplicateEmailsInDB);
        }

        // Display all validation errors at once
        if (errors.length > 0) {
          setUploadError(`Validation errors:\n${errors.join('\n')}`);
        }

        // Create users one by one (only non-duplicate users)
        let successCount = 0;
        let failCount = 0;
        const failErrors = [];

        for (const userData of finalUsersToCreate) {
          try {
            await api.post('/users', userData);
            successCount++;
          } catch (error) {
            failCount++;
            const errorMsg = error.response?.data?.error || error.message;
            // Check if it's a duplicate error from backend (additional safety check)
            if (errorMsg.toLowerCase().includes('duplicate') || errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('email')) {
              failErrors.push(`${userData.email}: Email already exists`);
            } else {
              failErrors.push(`${userData.email}: ${errorMsg}`);
            }
          }
        }

        // Consolidate all error messages
        const allErrors = [];
        if (errors.length > 0) {
          allErrors.push(`Validation errors:\n${errors.join('\n')}`);
        }
        if (failCount > 0) {
          allErrors.push(`\nFailed to import ${failCount} user(s):\n${failErrors.join('\n')}`);
        }

        if (allErrors.length > 0) {
          setUploadError(allErrors.join('\n\n'));
        }

        if (successCount > 0) {
          setUploadSuccess(`Successfully imported ${successCount} user(s)`);
          fetchUsers();
        }
      }

      // Reset file input
      event.target.value = '';
    } catch (error) {
      setUploadError(`Error reading file: ${error.message}`);
      event.target.value = '';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Box display="flex" gap={2}>
          <Button 
            variant="outlined" 
            startIcon={<UploadFileIcon />} 
            onClick={() => setUploadDialogOpen(true)}
          >
            Bulk Upload
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleExportExcel}
            disabled={filteredUsers.length === 0}
          >
            Download Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add User
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="body1">
          Showing <strong>{filteredUsers.length}</strong> user(s) out of <strong>{users.length}</strong>
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            disabled={!filterState && !filterDistrict && !filterArea && !filterGender && !filterEmail && !filterPhone}
          >
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setFilterDistrict('');
                }}
                label="State"
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name} ({users.filter(u => u.state_id === state.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>District</InputLabel>
              <Select
                value={filterDistrict}
                onChange={(e) => setFilterDistrict(e.target.value)}
                label="District"
                disabled={!filterState}
              >
                <MenuItem value="">All Districts</MenuItem>
                {filterDistricts.map((district) => (
                  <MenuItem key={district.id} value={district.id}>
                    {district.name} ({users.filter(u => u.district_id === district.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Area"
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              placeholder="Search by area..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                label="Gender"
              >
                <MenuItem value="">All Genders ({users.length})</MenuItem>
                <MenuItem value="male">Male ({genderCounts.male || 0})</MenuItem>
                <MenuItem value="female">Female ({genderCounts.female || 0})</MenuItem>
                <MenuItem value="other">Other ({genderCounts.other || 0})</MenuItem>
                <MenuItem value="others">Others ({genderCounts.others || 0})</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search Email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Enter email..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Search Mobile"
              value={filterPhone}
              onChange={(e) => setFilterPhone(e.target.value)}
              placeholder="Enter mobile..."
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        data={filteredUsers}
        loading={loading}
        searchFields={['name', 'email', 'phone']}
        emptyMessage="No users found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'name', label: 'Name' },
          { field: 'email', label: 'Email' },
          { field: 'phone', label: 'Phone' },
          { field: 'service_type', label: 'Service Type' },
          { field: 'trainer_interactions', label: 'Trainer Interactions' },
          { field: 'source_website', label: 'Source Website' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(user) => (
          <TableRow key={user.id}>
            <TableCell>{user.id}</TableCell>
            <TableCell>{user.name}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell>{user.phone || '-'}</TableCell>
            <TableCell>{user.service_type || 'home_services'}</TableCell>
            <TableCell>
              {user.trainer_interactions ? (
                <Box>
                  <Typography variant="body2">
                    Views: {user.trainer_interactions.view_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Responses: {user.trainer_interactions.respond_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {user.trainer_interactions.total_count || 0}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">0</Typography>
              )}
            </TableCell>
            <TableCell>{user.source_website || '-'}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleViewOpen(user)}>
                <VisibilityIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleOpen(user)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(user.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      {/* Bulk Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        setUploadDialogOpen(false);
        setUploadError('');
        setUploadSuccess('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Users</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadSuccess}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload an Excel file (.xlsx) with the following columns:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><strong>Name</strong> (required)</li>
            <li><strong>Email</strong> (required)</li>
            <li>Phone</li>
            <li>Gender</li>
            <li>State</li>
            <li>District</li>
            <li>Area</li>
            <li>Pincode</li>
            <li>Service Type</li>
            <li>Start Plan</li>
            <li>Available Days</li>
            <li>Source Website</li>
            <li>Category</li>
            <li>Subcategories (comma-separated)</li>
          </Box>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="excel-upload-input"
          />
          <label htmlFor="excel-upload-input">
            <Button variant="contained" component="span" fullWidth>
              Choose Excel File
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadError('');
            setUploadSuccess('');
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender || ''}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              label="Gender"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Area"
            value={formData.area || ''}
            onChange={(e) => setFormData({ ...formData, area: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Pincode"
            value={formData.pincode || ''}
            onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
            margin="normal"
            inputProps={{ maxLength: 10 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>When do you plan to start Personal trainer?</InputLabel>
            <Select
              value={formData.start_plan || ''}
              onChange={(e) => setFormData({ ...formData, start_plan: e.target.value })}
              label="When do you plan to start Personal trainer?"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="immediately">Immediately</MenuItem>
              <MenuItem value="within_a_month">Within a Month</MenuItem>
              <MenuItem value="not_sure">Not Sure, Just want to look at options</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>What Days are you generally available to take classes?</InputLabel>
            <Select
              value={formData.available_days || ''}
              onChange={(e) => setFormData({ ...formData, available_days: e.target.value })}
              label="What Days are you generally available to take classes?"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="any_day">Any day</MenuItem>
              <MenuItem value="weekday">Weekday</MenuItem>
              <MenuItem value="weekend">Weekend</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>State</InputLabel>
            <Select
              value={formData.state_id || ''}
              onChange={async (e) => {
                const stateId = e.target.value;
                setFormData({
                  ...formData,
                  state_id: stateId,
                  district_id: '',
                  city_id: ''
                });
                await fetchDistricts(stateId);
              }}
              label="State"
            >
              <MenuItem value="">None</MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>District</InputLabel>
            <Select
              value={formData.district_id || ''}
              onChange={async (e) => {
                const districtId = e.target.value;
                setFormData({
                  ...formData,
                  district_id: districtId,
                  city_id: ''
                });
                await fetchCities(districtId);
              }}
              label="District"
              disabled={!formData.state_id}
            >
              <MenuItem value="">None</MenuItem>
              {districts.map((district) => (
                <MenuItem key={district.id} value={district.id}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControl fullWidth margin="normal">
            <InputLabel>City</InputLabel>
            <Select
              value={formData.city_id || ''}
              onChange={(e) => {
                setFormData({ ...formData, city_id: e.target.value });
              }}
              label="City"
              disabled={!formData.district_id}
            >
              <MenuItem value="">None</MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          {/* <TextField
            fullWidth
            label="District (Legacy)"
            value={formData.district || ''}
            onChange={(e) => setFormData({ ...formData, district: e.target.value })}
            margin="normal"
            helperText="Optional: Use if district dropdown is not available"
          />
          <TextField
            fullWidth
            label="City (Legacy)"
            value={formData.city || ''}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            margin="normal"
            helperText="Optional: Use if city dropdown is not available"
          /> */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Service Type</InputLabel>
            <Select
              value={formData.service_type}
              onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              label="Service Type"
            >
              <MenuItem value="home_services">Home Services</MenuItem>
              <MenuItem value="online_services">Online Services</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Professional Specializations</InputLabel>
            <Select
              value={formData.category_id || ''}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  category_id: e.target.value,
                  subcategory_ids: [] // Reset subcategories when category changes
                });
              }}
              label="CaProfessional Specializationstegory"
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Providing Services</InputLabel>
            <Select
              multiple
              value={formData.subcategory_ids || []}
              onChange={(e) => setFormData({ ...formData, subcategory_ids: e.target.value })}
              input={<OutlinedInput label="Providing Services" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const subcat = subcategories.find(s => s.id === value);
                    return subcat ? (
                      <Chip key={value} label={subcat.name} size="small" />
                    ) : null;
                  })}
                </Box>
              )}
              disabled={!formData.category_id}
            >
              {getSubcategoriesForCategory(formData.category_id).map((subcat) => (
                <MenuItem key={subcat.id} value={subcat.id}>
                  <Checkbox checked={formData.subcategory_ids?.indexOf(subcat.id) > -1} />
                  <ListItemText primary={subcat.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <TextField
            fullWidth
            label="Source Website"
            value={formData.source_website || ''}
            onChange={(e) => setFormData({ ...formData, source_website: e.target.value })}
            margin="normal"
            readOnly
          /> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {viewUser && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewUser.id}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {viewUser.name || '-'}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {viewUser.email || '-'}</Typography>
              <Typography variant="body2"><strong>Phone:</strong> {viewUser.phone || '-'}</Typography>
              <Typography variant="body2"><strong>Gender:</strong> {viewUser.gender || '-'}</Typography>
              <Typography variant="body2"><strong>Service Type:</strong> {viewUser.service_type || '-'}</Typography>
              <Typography variant="body2"><strong>Source Website:</strong> {viewUser.source_website || '-'}</Typography>
              <Typography variant="body2"><strong>Area:</strong> {viewUser.area || '-'}</Typography>
              <Typography variant="body2"><strong>Pincode:</strong> {viewUser.pincode || '-'}</Typography>
              <Typography variant="body2"><strong>Start Plan:</strong> {viewUser.start_plan || '-'}</Typography>
              <Typography variant="body2"><strong>Available Days:</strong> {viewUser.available_days || '-'}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Subcategories:</strong></Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {viewUser.subcategories?.length
                  ? viewUser.subcategories.map((s) => <Chip key={s.id} label={s.name} size="small" />)
                  : <Typography variant="body2">-</Typography>}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;